import { useLayoutEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';

import useI18n from '@i18n';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const ReactMarkdown = dynamic(() => import('react-markdown'), {
    ssr: false,
    loading: () => (
        <div className="space-y-2">
            <div className="w-full h-4 bg-blueGray-100 rounded-2 animate-pulse" />
            <div className="w-full h-4 bg-blueGray-100 rounded-2 animate-pulse" />
        </div>
    ),
});

interface INotificationCardProps {
    href: string;
}

const NotificationCard: React.FC<INotificationCardProps> = ({ children, href }) => {
    const { t } = useI18n('Base');
    const contentRef = useRef<HTMLDivElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useLayoutEffect(() => {
        const element = contentRef.current;

        if (!element) {
            return undefined;
        }

        const checkTruncation = () => {
            setIsTruncated(element.scrollHeight > element.clientHeight);
        };

        checkTruncation();

        const resizeObserver = new ResizeObserver(checkTruncation);
        resizeObserver.observe(element);

        const mutationObserver = new MutationObserver(checkTruncation);
        mutationObserver.observe(element, { childList: true, subtree: true, characterData: true });

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [children]);

    return (
        <div className="p-4 border-b-[1px] border-blueGray-100 hover:bg-blueGray-100">
            <div
                ref={contentRef}
                className="mb-1 overflow-hidden text-blueGray-800 font-p1 line-clamp-2"
            >
                <ReactMarkdown
                    components={{
                        p: props => <p className="mb-2 last:mb-0">{props.children}</p>,
                    }}
                >
                    {children as string}
                </ReactMarkdown>
            </div>
            {isTruncated && href && (
                <a className="underline text-blueGray-800" href={href}>
                    {t('More details')}
                </a>
            )}
        </div>
    );
};

export default NotificationCard;
